import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import EntityCard from '../common/EntityCard';
import TrendSparkline from '../common/TrendSparkline';
import InsightsPanel from '../common/InsightsPanel';
import StatusChip from '../common/StatusChip';
import { categorySpendSeries, categoryBudgetByMonth, averageMonthlyValue } from '../../utils/entityTrendSeries';
import buildCategoryAnalysisPayload from '../../utils/categoryAnalysisPayload';
import api from '../../services/api';

/*
  CategoryCard: one category as a grid card — identity row, average monthly
  spend for the selected range, a smoothed spend line against the stepped
  budget guide, and collapsible AI insights (same payload as the analysis
  dialog, so both share the backend cache). Drag anywhere on the card to
  reorder, exactly like the old rows.
*/

const money2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const VALUE_LABELS = { spend: 'Spent', income: 'Received', net: 'Net' };

const CategoryCard = ({
  category,
  index,
  monthKeys,
  range,
  budgetData,
  budgets,
  transactions,
  includePartialMonth = true,
  onEdit,
  onDelete,
  onAnalyze,
  moveCard,
  selectable = false,
  selected = false,
  onToggleSelect,
}) => {
  const isProtected = ['income', 'expense'].includes(category.id);
  const ref = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'CATEGORY',
    item: () => ({
      index,
      id: category.id,
      name: category.name,
      color: category.color,
      width: ref.current?.offsetWidth,
    }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    canDrag: !isProtected,
  });

  // Suppress the browser's drag snapshot; DragPreviewLayer renders instead
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const [{ handlerId }, drop] = useDrop({
    accept: 'CATEGORY',
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover: (draggedItem, monitor) => {
      if (!ref.current) return;
      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      // Grid variant of the list midpoint rule: only move once the pointer
      // has crossed the hovered card's centre in the direction of travel.
      const rect = ref.current.getBoundingClientRect();
      const midX = (rect.right - rect.left) / 2;
      const midY = (rect.bottom - rect.top) / 2;
      const offset = monitor.getClientOffset();
      const x = offset.x - rect.left;
      const y = offset.y - rect.top;
      if (dragIndex < hoverIndex && y < midY && x < midX) return;
      if (dragIndex > hoverIndex && y > midY && x > midX) return;
      moveCard(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  drag(drop(ref));

  const series = useMemo(
    () => categorySpendSeries(category, budgetData, monthKeys),
    [category, budgetData, monthKeys]
  );
  const budgetGuide = useMemo(
    () => categoryBudgetByMonth(category.id, budgets, monthKeys),
    [category.id, budgets, monthKeys]
  );
  const average = useMemo(
    () => averageMonthlyValue(series.points, { includePartialMonth }),
    [series, includePartialMonth]
  );

  // Same payload the analysis dialog sends → same backend cache entry.
  const analysisPayload = useMemo(
    () => buildCategoryAnalysisPayload(category, budgetData, transactions),
    [category, budgetData, transactions]
  );
  const fetchAnalysis = useCallback(
    async (force) => {
      const { data } = await api.post('/ai/category-analysis', {
        categoryId: category.id,
        data: analysisPayload,
        force,
      });
      return data;
    },
    [category.id, analysisPayload]
  );

  return (
    <EntityCard
      cardRef={ref}
      handlerId={handlerId}
      isDragging={isDragging}
      canDrag={!isProtected}
      color={category.color}
      name={category.name}
      chips={
        <StatusChip
          status={category.isTransfer ? 'neutral' : String(category.type).toLowerCase() === 'income' ? 'success' : 'error'}
          label={category.isTransfer ? 'Transfer' : category.type}
          sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }}
        />
      }
      menuItems={[
        {
          key: 'analyze',
          label: 'Analyze with AI',
          icon: <AutoAwesomeIcon fontSize="small" />,
          onClick: () => onAnalyze(category),
          disabled: Boolean(category.isTransfer),
          tooltip: category.isTransfer ? 'Transfers are excluded from income/expense analysis' : undefined,
        },
        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEdit(category) },
        {
          key: 'delete',
          label: 'Delete',
          icon: <DeleteIcon fontSize="small" />,
          onClick: () => onDelete(category),
          disabled: isProtected,
          tooltip: isProtected ? 'This is a system category and cannot be deleted' : undefined,
        },
      ]}
      statLabel={`Avg / month · ${range}`}
      statValue={money2.format(average)}
      chart={
        series.hasActivity ? (
          <TrendSparkline
            points={series.points}
            budgets={budgetGuide}
            color={category.color}
            valueLabel={VALUE_LABELS[series.meaning]}
          />
        ) : (
          <Box
            sx={{
              height: 74,
              borderRadius: 2,
              border: '1.5px dashed',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="text.disabled">
              No activity in this window
            </Typography>
          </Box>
        )
      }
      footer={
        <InsightsPanel
          storageKey={`category:${category.id}`}
          fetchAnalysis={fetchAnalysis}
          disabled={Boolean(category.isTransfer) || !analysisPayload || analysisPayload.noData}
        />
      }
      selectable={selectable && !isProtected}
      selected={selected}
      onToggleSelect={() => onToggleSelect && onToggleSelect(category.id)}
    />
  );
};

export default CategoryCard;
